<template>
  <div class="relative min-w-0">
    <div @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" @mousemove="handleMouseMove" class="min-w-0">
      <slot />
    </div>
    <transition>
      <div v-if="showTooltip" :style="tooltipPosition">
        <!-- Tooltip Content Slot (if provided) -->
        <template v-if="$slots.tooltip || $scopedSlots.tooltip">
          <slot name="tooltip" />
        </template>
        <!-- Default Text Content -->
        <template v-else>
          <div class="default-tooltip-content w-max shadow-md">
            <BaseText type="label" size="xs" class="text-white px-2 py-1">
              {{ text }}
            </BaseText>
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
const INVERSE_POSITIONS = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left'
}

export default {
  name: 'BaseTooltipV2',
  props: {
    // Whether the tooltip behavior is enabled
    tooltipEnabled: {
      type: Boolean,
      default: true
    },
    // (Optional) Provide text to display with the default styling
    text: {
      type: String,
      default: ''
    },
    // 'top' | 'bottom' | 'left' | 'right' | 'cursor' | 'cursor-tracking'
    position: {
      type: String,
      default: 'top'
    },
    // 'start' | 'center' | 'end'
    alignment: {
      type: String,
      default: 'center'
    },
    // Offset distance from the reference element
    offset: {
      type: [Number, String],
      default: 6 // 6px
    },
    // Delay (ms) before showing the tooltip after hover
    delay: {
      type: Number,
      default: 0
    },
    // Z-index override
    zIndex: {
      type: Number,
      default: 1000
    }
  },
  data () {
    return {
      showTooltip: false,
      displayTimeout: null,
      cursorX: 0,
      cursorY: 0
    }
  },
  beforeDestroy () {
    clearTimeout(this.displayTimeout)
  },
  watch: {
    tooltipEnabled (enabled) {
      if (!enabled) {
        this.showTooltip = false
        clearTimeout(this.displayTimeout)
      }
    }
  },
  computed: {
    tooltipPosition () {
      // Cursor-anchored tooltip: float above the pointer
      if (['cursor', 'cursor-tracking'].includes(this.position)) {
        const alignmentTransformMap = {
          start: '',
          center: 'translateX(-50%)',
          end: 'translateX(-100%)'
        }
        return {
          position: 'fixed',
          pointerEvents: 'none',
          zIndex: this.zIndex,
          top: `${this.cursorY - Number(this.offset)}px`,
          left: `${this.cursorX}px`,
          transform: `translateY(-100%) ${alignmentTransformMap[this.alignment] || ''}`.trim()
        }
      }
      const alignmentStyleMap = ['top', 'bottom'].includes(this.position)
        ? {
          start: { left: '0px' },
          center: { left: '50%', transform: 'translateX(-50%)' },
          end: { right: '0px' }
        }
        : {
          start: { top: '0px' },
          center: { top: '50%', transform: 'translateY(-50%)' },
          end: { bottom: '0px' }
        }
      return {
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: this.zIndex,
        // Add the positioning information
        [INVERSE_POSITIONS[this.position]]: `calc(100% + ${this.offset}px)`,
        ...alignmentStyleMap[this.alignment]
      }
    }
  },
  methods: {
    handleMouseEnter () {
      if (!this.tooltipEnabled) return
      clearTimeout(this.displayTimeout)
      this.displayTimeout = setTimeout(() => {
        this.showTooltip = true
      }, this.delay)
    },
    handleMouseLeave () {
      if (!this.tooltipEnabled) return
      clearTimeout(this.displayTimeout)
      this.showTooltip = false
    },
    handleMouseMove (event) {
      if (!this.tooltipEnabled || !['cursor', 'cursor-tracking'].includes(this.position)) return
      if (this.showTooltip && this.position !== 'cursor-tracking') {
        this.showTooltip = false
        clearTimeout(this.displayTimeout)
        this.displayTimeout = setTimeout(() => {
          this.showTooltip = true
        }, this.delay * 1.5)
      }
      this.cursorX = event.clientX
      this.cursorY = event.clientY
    }
  }
}
</script>

<style scoped>
.default-tooltip-content {
  border-radius: 8px;
  background: rgba(6, 7, 16, 0.88);
  backdrop-filter: blur(6px);
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>